/**
 * Personalized distribution — api-service wire contract (snake_case) and shared vocabulary.
 *
 * One story → one personalized copy per recipient, filtered to their value of the split field
 * (e.g. Region = West), in their branding, template and format, sent by email or Slack, now or
 * on a schedule. A tile that can't take the filter is left out of the copy, never sent unfiltered.
 *
 * Base path: /api/v2/story/{story_id}/distributions — story owner only; 404 when the tenant runs
 * the old architecture (`platform.old_architecture`).
 *
 * Writes never wait on a workflow: they answer 202 with a `request_id`; poll
 * GET /edits/{request_id} until `done`, then re-read. Reads answer 200 from stored rows.
 *
 *   GET    ""                                            ?status=archived → DistributionListApi
 *   POST   ""                                            DistributionCreateApi → 202 WriteAcceptedApi
 *   GET    "/edits/{request_id}"                         → EditStatusApi
 *   POST   "/split-check"                                SplitCheckRequestApi → SplitCheckApi
 *   POST   "/value-stats"                                ValueStatsRequestApi → 202 { request_id }
 *   GET    "/value-stats/{request_id}"                   → ValueStatsApi
 *   POST   "/schedule-preview"                           SchedulePreviewRequestApi → SchedulePreviewApi
 *   GET    "/{distribution_id}"                          → DistributionApi
 *   PATCH  "/{distribution_id}"                          DistributionPatchApi → 202 WriteAcceptedApi
 *   DELETE "/{distribution_id}"                          → 202 WriteAcceptedApi (archives, removes its schedule)
 *   POST   "/{distribution_id}/status"                   DistributionStatusRequestApi → 202 WriteAcceptedApi
 *   POST   "/{distribution_id}/restore"                  → 202 WriteAcceptedApi (archived → paused if ever activated, else draft)
 *   PUT    "/{distribution_id}/schedule"                 DistributionScheduleApi | null → 202 WriteAcceptedApi
 *   GET    "/{distribution_id}/recipients"               ?offset&limit → RecipientPageApi
 *   PUT    "/{distribution_id}/recipients"               RecipientsPutApi → 202 WriteAcceptedApi
 *   POST   "/{distribution_id}/runs"                     RunRequestApi → 202 RunAcceptedApi | 409 conflict
 *   GET    "/{distribution_id}/runs"                     ?offset&limit&trigger*&status* → RunPageApi
 *   GET    "/{distribution_id}/runs/{run_id}"            → RunDetailApi
 *   GET    "/{distribution_id}/runs/{run_id}/slices/{slice_id}"               → SliceDetailApi
 *   GET    "/{distribution_id}/runs/{run_id}/slices/{slice_id}/files/{format}" → SignedFileApi
 *   GET    "/{distribution_id}/runs/{run_id}/files.zip?format=all|pdf|pptx"    → application/zip (404 no files; 413 zip_too_large)
 *
 * Tenant templates (base /api/v2/distribution-templates):
 *   GET    ""                                            → TemplateListApi (built-ins first)
 *   POST   ""                                            TemplateWriteApi → 202 TemplateWriteAcceptedApi
 *   PATCH  "/{template_id}"                              TemplateWriteApi → 202 TemplateWriteAcceptedApi
 *   DELETE "/{template_id}"                              → 202 TemplateWriteAcceptedApi
 *
 *   GET    "/split-fields?audience=inside|outside"       → SplitFieldListApi
 *
 * Deck themes (GET /api/v2/distribution-themes → DistributionThemeListApi): a theme's `id` is the
 * action-engine catalog key and is what a recipient row or content sends back as `theme_id`.
 *
 * Every write sends an `Idempotency-Key` (a UUID per user action; a retry reuses it). Template
 * writes also send `?story_id=` when the builder was opened from a story.
 * Errors use api-service's body; the UI shows `message` verbatim (getApiErrorMessage) and reads
 * per-row `details` through getApiErrorDetails.
 */
import type { FieldRefApi } from "../storyCanvas/storyCanvas.types";

// ─── Shared vocabulary ────────────────────────────────────────────────────────

export type DistributionStatusApi = "draft" | "active" | "paused" | "completed" | "archived";

/** `email`, `slack` and `teams` send today; the others show as "Coming soon" and are refused. */
export type ChannelApi = "email" | "slack" | "teams" | "link" | "drive";
export const LIVE_CHANNELS: readonly ChannelApi[] = ["email", "slack", "teams"];

/**
 * Host endings a Teams webhook address may have. Mirrors magic-core TEAMS_WEBHOOK_HOSTS
 * (magicbi_core.schemas.distribution.recipients, 2.62.0); change both together.
 */
export const TEAMS_WEBHOOK_HOSTS: readonly string[] = [".logic.azure.com", ".api.powerplatform.com", ".webhook.office.com"];
/** Longest Teams webhook address, as magic-core. */
export const TEAMS_WEBHOOK_MAX_LENGTH = 2048;

export type FormatApi = "pdf" | "pptx";

/**
 * Who receives the copies: people in the tenant's own organisation (`inside`) or people outside
 * it (`outside`). It sets the class every copy and the value stats are redacted at: the tenant's
 * inside clearance (confidential by default) or its recipient clearance (internal by default),
 * never above what the owner may see themselves.
 */
export type AudienceApi = "inside" | "outside";
/** Content saved before the choice existed reads as `outside`, the stricter. */
export const DEFAULT_AUDIENCE: AudienceApi = "outside";

/** How sensitive a value is, lowest first (magic-core `SensitivityClass`). */
export type SensitivityClassApi = "public" | "internal" | "confidential" | "restricted_pii";

/**
 * Tokens every message may use, in magic-core `TOKENS` order (2.41.0). Extra recipient columns
 * become tokens too, and `kpi:<name>` per KPI tile of the Story (`TokenListApi`).
 */
export const BUILT_IN_TOKENS = [
  "name",
  "first_name",
  "last_name",
  "email",
  "value",
  "field",
  "story",
  "date",
  "data_as_of",
  "next_send",
  "sender",
  "sender_email",
  "company",
  "summary",
] as const;
export type BuiltInTokenApi = (typeof BUILT_IN_TOKENS)[number];

export const LIMITS = {
  /**
   * Also the page size for reading recipients back. api-service caps `limit` at 500 per page and
   * the tenant's `max_recipients` setting must stay ≤500: above that, rows past 500 are never
   * fetched because the UI reads a single page (wire report A10).
   */
  maxRecipients: 500,
  maxCsvBytes: 1_048_576,
  maxCc: 5,
  maxExtraColumns: 20,
  subjectMax: 200,
  bodyMax: 10_000,
  slackTextMax: 3_000,
} as const;

export interface UserRefApi {
  id: string;
  name: string | null;
  email: string | null;
}

// ─── Writes are accepted, then polled ─────────────────────────────────────────

export interface WriteAcceptedApi {
  distribution_id: string;
  request_id: string;
}

export interface EditStatusApi {
  request_id: string;
  status: "pending" | "done" | "failed";
  /** User-facing; shown verbatim. */
  error: { code: string; message: string } | null;
}

// ─── Split field, values, tiles ───────────────────────────────────────────────

export interface SplitFieldApi {
  field: FieldRefApi;
  model_release_id: string;
  /** "Region" */
  label: string;
  /** "Sales region the store belongs to." */
  description: string | null;
  /** Where it reads from, shown small and monospace: "dim_store.region". */
  source_ref: string | null;
}

/**
 * One field the story can be split by, as listed for an audience. The extra keys are list-only:
 * a write (create, PATCH, split check, value stats) sends the `SplitFieldApi` part alone, since
 * magic-core's `SplitField` refuses unknown keys (`toSplitField`).
 */
export interface SplitFieldOptionApi extends SplitFieldApi {
  sensitivity_class: SensitivityClassApi | null;
  /** Its values are above the audience's clearance: listed, but it can't split the copies. */
  above_clearance: boolean;
  /** Why it can't split, shown verbatim; null when it can. */
  blocked_reason: string | null;
  /** `label`, with its table added when two fields share a label. */
  display_label: string;
}

export interface SplitFieldListApi {
  items: SplitFieldOptionApi[];
  /** Round 6 (step 1 items): the class each audience's copies are redacted at, for this caller. */
  clearances: AudienceClearancesApi;
}

// ─── Round 6 · step 1 items and audience wording (FE-items) ───────────────────

/**
 * What each audience's copies may show, for the caller: the tenant's setting capped at the
 * caller's own clearance. Step 1's "what is masked" wording reads it; it never assumes defaults.
 */
export interface AudienceClearancesApi {
  inside: SensitivityClassApi;
  outside: SensitivityClassApi;
}

// ─── End round 6 · step 1 items (FE-items) ────────────────────────────────────

export interface DistributionThemeApi {
  /** The catalog key; sent back as `theme_id`. */
  id: string;
  name: string;
  description: string | null;
  tone: "light" | "dark";
  /**
   * Round 7 (pd7 CONTRACT §2): the theme file's palette verbatim — colour token → `#rrggbb`, and
   * `chartRamp` → series colours. Absent from older answers.
   */
  palette?: Record<string, string | string[]>;
  font_family?: string | null;
  /** The theme's own font files, read through the API with the signed-in client. */
  fonts?: DistributionThemeFontApi[];
  /** null until action-engine has built the theme's preview kit. */
  kit?: DistributionThemeKitApi | null;
}

export interface DistributionThemeFontApi {
  family: string;
  weight: string;
  style: string;
  url: string;
}

export interface DistributionThemeKitBoxApi {
  token: string;
  role:
    | "title"
    | "subtitle"
    | "meta"
    | "body"
    | "visual"
    | "kpi_label"
    | "kpi_value"
    | "kpi_delta"
    | "caption"
    | "url"
    | "image"
    | "other";
  index: number | null;
  /** The box's whole text with `{{TOKEN}}`s in place ("PAGE {{PAGE}}"); null when it is only its token. */
  template?: string | null;
  /** Fractions (0–1) of the slide, from its top-left corner. */
  x: number;
  y: number;
  w: number;
  h: number;
  font_family: string | null;
  font_pt: number;
  bold: boolean;
  italic: boolean;
  color: string | null;
  align: "left" | "center" | "right";
  valign: "top" | "middle" | "bottom";
}

export interface DistributionThemeKitSlideApi {
  /** Signed; the slide's artwork without its text boxes and mark. */
  background_url: string;
  /** Numbered-group slides (kpi_row3): artwork keeping only the first n groups, by n ("1", "2"). */
  variants: Record<string, { background_url: string }> | null;
  mark: { x: number; y: number; w: number; h: number; image_url: string | null } | null;
  boxes: DistributionThemeKitBoxApi[];
}

export interface DistributionThemeKitApi {
  version: number;
  /** Built from an older template file than the theme's current one. */
  stale: boolean;
  slide: { width_pt: number; height_pt: number };
  /** By the theme file's slide kind: cover_open, kpi_row3, chart_full, chart_split, text_body, … */
  slides: Record<string, DistributionThemeKitSlideApi>;
}

export interface DistributionThemeListApi {
  items: DistributionThemeApi[];
}

export type TileKindApi = "kpi" | "chart" | "table" | "note" | "link" | "bi_view" | "still";

export type LeftOutReasonApi =
  | "bi_view" //          an embedded Tableau/Power BI view can't be filtered by MagicBI
  | "still" //            a captured image is a picture of every slice
  | "static_item" //      notes and links read the same for everyone (owner may opt in)
  | "keeps_own_filter" // the tile keeps its own condition on the split field
  | "not_reachable" //    its data doesn't connect to the split field
  | "refused" //          the tile's query refused the filter when it ran
  | "too_many_rows"
  | "not_drawable"
  | "masked" //         every value it shows is above the audience's clearance
  | "turned_off"; //    the owner turned it off in step 1 (`content.excluded_item_ids`)

export interface TileRefApi {
  item_id: string;
  title: string;
  kind: TileKindApi;
  /** Holds values above the recipient clearance; shown masked in every copy. */
  sensitive: boolean;
}

export interface LeftOutTileApi extends TileRefApi {
  reason: LeftOutReasonApi;
  /** Server copy, shown verbatim. */
  message: string;
  /** Notes, links and stills may be sent as they are when the owner opts them in. */
  can_opt_in: boolean;
}

export interface SplitCheckRequestApi {
  split: SplitFieldApi;
  /** A tile whose every value is above this audience's clearance is left out as `masked`. */
  audience: AudienceApi;
}

/** Computed from the story's tiles; no query runs. The exact list comes from a preview run. */
export interface SplitCheckApi {
  total_tiles: number;
  personalized: TileRefApi[];
  left_out: LeftOutTileApi[];
}

export interface ValueStatsRequestApi {
  split: SplitFieldApi;
  /** The values and numbers are redacted at this audience's clearance. */
  audience: AudienceApi;
}

export interface ValueKpiApi {
  label: string;
  /** Formatted by the server ("$14.8M", "32.4%"); null when masked. */
  formatted: string | null;
  delta_formatted: string | null;
  direction: "up" | "down" | "flat" | null;
  masked: boolean;
}

export interface ValueStatApi {
  value: string;
  /** Fact rows behind the value; null when unknown. */
  rows: number | null;
  /** Up to 3 of the story's KPI tiles, computed for this value. */
  kpis: ValueKpiApi[];
  /** Up to 12 points of the story's first trend chart, for the quick preview bars. */
  trend: number[] | null;
}

export interface ValueStatsApi {
  request_id: string;
  status: "pending" | "done" | "failed";
  /** When the data behind the numbers was loaded ("from the Oct 13, 6:00 AM refresh"). */
  as_of: string | null;
  values: ValueStatApi[];
  /**
   * Set when `failed`; the message is shown verbatim. `SPLIT_FIELD_MASKED`: the field's values are
   * above the audience's clearance. `TOO_MANY_VALUES`: more values than the tenant lists.
   */
  error: { code: string; message: string } | null;
}

// ─── Recipients ───────────────────────────────────────────────────────────────

export type RecipientSourceKindApi = "csv" | "manual" | "model";

export interface RecipientBrandApi {
  /** Shown in the copy's band, e.g. "Central Co-op". */
  name: string | null;
  /** https PNG/JPG; fetched server-side with an SSRF guard. */
  logo_url: string | null;
  /** "#RRGGBB" */
  accent: string | null;
  /** 1–2 letters shown when there is no logo. */
  mark: string | null;
  // ─── Round 6 · brand kits (WP7) ─────────────────────────────────────────────
  /**
   * A tenant brand kit (distributionBrandKits.types.ts). Story Manager fills what the fields above
   * leave blank from it: display name, mark, logo and palette. A new distribution starts with the
   * default kit (step 3).
   */
  kit_id?: string | null;
  /** One of the kit's logos; null → the kit's first light logo. */
  logo_id?: string | null;
  /** One of the kit's palettes; null → the kit's first. */
  palette_id?: string | null;
  /** Resolved by Story Manager; api-service refuses a non-null value in a person's input. */
  logo_object_key?: string | null;
  // ─── end round 6 · brand kits ───────────────────────────────────────────────
}

/** One row as the user edits it (CSV line or manual row). */
export interface RecipientInputApi {
  /** 1-based order in the list; stable key for deliveries. */
  position: number;
  /** A value of the split field; matched trimmed and case-insensitively, echoed canonical. Absent with no split field. */
  value?: string;
  name: string;
  channel: ChannelApi;
  /** Email address, or a Slack channel as "#name" or its id. */
  address: string;
  /** Email only. */
  cc: string[];
  /** null → the distribution's default formats. */
  formats: FormatApi[] | null;
  /** null → the distribution's default template. */
  template_id: string | null;
  /** null → the distribution's default theme. */
  theme_id: string | null;
  /** null → the distribution's default branding. */
  brand: RecipientBrandApi | null;
  /** Extra columns, keys normalised to lower snake_case; each becomes {{key}}. */
  extra: Record<string, string>;
  source: RecipientSourceKindApi;
}

export type RecipientIssueCodeApi =
  | "missing_name"
  | "missing_value"
  | "value_not_in_data"
  | "value_excluded"
  | "value_normalized"
  | "missing_address"
  | "invalid_email"
  | "invalid_slack_channel"
  | "invalid_teams_webhook"
  | "slack_not_connected"
  | "slack_channel_not_found"
  | "slack_app_not_in_channel"
  | "channel_coming_soon"
  | "invalid_format"
  | "unknown_template"
  | "unknown_theme"
  | "invalid_logo_url"
  | "invalid_accent"
  | "invalid_cc"
  | "cc_ignored"
  | "duplicate"
  /**
   * Round 6: with an `inside` audience, an email address or cc outside the tenant's own domains
   * (the email domains of its active members). Server-only: the browser doesn't know the domains.
   */
  | "outside_address";

export interface RecipientIssueApi {
  code: RecipientIssueCodeApi;
  /** `error` blocks activation (not saving); `warning` sends with the stated fallback. */
  severity: "error" | "warning";
  column: keyof RecipientInputApi | null;
  /** Says what's wrong and how to fix it; shown verbatim. */
  message: string;
}

export interface RecipientApi extends RecipientInputApi {
  id: string;
  status: "ready" | "fix";
  issues: RecipientIssueApi[];
  /** Resolved "#name" for Slack rows. */
  slack_channel_name: string | null;
}

export interface RecipientSourceInfoApi {
  /** `model` is "Coming soon": refused with 422 until supported. */
  kind: "list" | "model";
  file_name: string | null;
  uploaded_at: string | null;
  /** CSV columns matched to recipient fields, e.g. { team: "extra.team" }. */
  matched_columns: Record<string, string>;
  /** Round 6: where the `model` rows come from; absent or null when none came from the data model. */
  model?: ModelRecipientSourceApi | null;
}

/** Full replace. Rows with problems are kept and reported: they block activation, not saving. */
export interface RecipientsPutApi {
  source: Omit<RecipientSourceInfoApi, "uploaded_at">;
  rows: RecipientInputApi[];
}

export interface RecipientPageApi {
  items: RecipientApi[];
  total: number;
  offset: number;
  limit: number;
  summary: RecipientSummaryApi;
}

export interface RecipientSummaryApi {
  total: number;
  ready: number;
  to_fix: number;
  by_channel: Partial<Record<ChannelApi, number>>;
}

export type CoverageStateApi = "ok" | "fix" | "none" | "excluded";

export interface CoverageValueApi {
  value: string;
  recipients: number;
  to_fix: number;
  state: CoverageStateApi;
}

export interface CoverageApi {
  /** `unknown` when the split field's values couldn't be listed; never blocks saving. */
  state: "known" | "unknown";
  total_values: number;
  covered_values: number;
  by_value: CoverageValueApi[];
  /** Included values with no recipient. */
  uncovered: string[];
  /** Values in the list that aren't in the data. */
  unknown_values: string[];
}

/** What happens to an included value nobody receives. */
export type UncoveredPolicyApi = "hold" | "skip";

// ─── Content, templates, branding ─────────────────────────────────────────────

export type TemplateKindApi = "deck" | "memo" | "video";

/** Fixed slide layouts; each has typed slots. */
export type SlideLayoutApi =
  | "cover"
  | "summary"
  | "kpi3"
  | "chart1"
  | "chart2"
  | "charttext"
  | "table"
  | "grid";

export const LAYOUT_SLOTS: Record<Exclude<SlideLayoutApi, "grid">, readonly SlotTypeApi[]> = {
  cover: ["cover"],
  summary: ["text"],
  kpi3: ["kpi", "kpi", "kpi"],
  chart1: ["chart"],
  chart2: ["chart", "chart"],
  charttext: ["chart", "text"],
  table: ["table"],
};

export type SlotTypeApi = "cover" | "text" | "kpi" | "chart" | "table";

export type BoxTypeApi = Exclude<SlotTypeApi, "cover">;

/** One box of a free-grid slide, in grid cells (0-based col/row). */
export interface GridBoxApi {
  col: number;
  row: number;
  cols: number;
  rows: number;
  type: BoxTypeApi;
}

export const GRID_COLS = 12;
export const GRID_ROWS = 6;
export const MAX_BOXES = 12;

/** A slot holds a story tile id, the generated AI summary or cover, or the owner's own text. */
export type SlotFillApi =
  | { kind: "tile"; item_id: string }
  | { kind: "ai_summary" }
  | { kind: "cover" }
  | { kind: "text"; text: string };

export interface SlideSpecApi {
  title: string;
  layout: SlideLayoutApi;
  /** One per slot of the layout, in order; null = empty slot (slide left out if all empty). */
  slots: (SlotFillApi | null)[];
  /** Free-grid slides: one slot per box, in order. "grid" layout <=> boxes set. */
  boxes?: GridBoxApi[] | null;
}

/** Slot types of a slide: its boxes for a grid slide, else the fixed layout's slots. */
export function slotTypesOf(spec: SlideSpecApi): SlotTypeApi[] {
  if (spec.layout === "grid") return (spec.boxes ?? []).map((b) => b.type);
  return [...LAYOUT_SLOTS[spec.layout]];
}

export type TemplateSourceApi = "built_in" | "layouts" | "pptx_master";

export interface TemplateApi {
  /** Built-ins: "story", "qbr", "weekly", "memo"; tenant templates: uuid. */
  id: string;
  name: string;
  kind: TemplateKindApi;
  source: TemplateSourceApi;
  description: string;
  slide_count: number;
  /** null for "story" (one slide per personalized tile, in story order). */
  slides_spec: SlideSpecApi[] | null;
  /** pptx_master only: the uploaded master's file name. */
  master_file_name: string | null;
  created_by: UserRefApi | null;
  updated_at: string | null;
  /** Round 7 (pd7 CONTRACT §2.4): pptx_master only — the upload and the design read from it. */
  master_id?: string | null;
  look?: DistributionMasterLookApi | null;
  /** Non-archived distributions using this template, built-ins included. Absent on older api-service builds. */
  used_by?: number;
}

/** Round 7: an uploaded PowerPoint template once action-engine has read it (pd7 CONTRACT §1.4, §2.4). */
export interface DistributionMasterLookApi {
  master_id: string;
  state: "preparing" | "ready" | "failed";
  /** The reader's user-facing reason when it failed, or a note about the match; null for none. */
  message: string | null;
  file_name: string;
  /** The upload's layout used for each slide kind, by kind; null until read. */
  layouts: Record<string, string> | null;
  tone?: "light" | "dark";
  palette?: Record<string, string | string[]>;
  font_family?: string | null;
  fonts?: DistributionThemeFontApi[];
  kit?: DistributionThemeKitApi | null;
}

/** Round 7: `POST /api/v2/distribution-templates/masters` (multipart `file`). */
export interface DistributionMasterUploadApi {
  master_id: string;
  file_name: string;
  size_bytes: number;
}

export interface TemplateListApi {
  items: TemplateApi[];
}

export interface TemplateWriteApi {
  name: string;
  kind: Exclude<TemplateKindApi, "video">;
  source: Exclude<TemplateSourceApi, "built_in">;
  slides_spec: SlideSpecApi[];
  /** pptx_master only: the `master_id` from `POST /distribution-templates/masters`. */
  master_upload_id?: string | null;
}

export interface TemplateWriteAcceptedApi {
  template_id: string;
  request_id: string;
}

export interface IncludeApi {
  /** Attach the files (PDF / PPTX). */
  files: boolean;
  /** A secure link to the online copy — "Coming soon"; stored, not sent. */
  link: boolean;
  /** The personalized message body. */
  message: boolean;
}

export interface MessageTemplatesApi {
  email_subject: string;
  email_body: string;
  slack_text: string;
}

export interface DistributionContentApi {
  template_id: string;
  theme_id: string | null;
  formats: FormatApi[];
  include: IncludeApi;
  include_ai_summary: boolean;
  /** Up to `max_snapshots` charts drawn inside the email body. */
  include_snapshots: boolean;
  /**
   * Who receives the copies, chosen in step 1. Content saved before the choice existed has none
   * and reads as `outside` (`audienceOf`).
   */
  audience?: AudienceApi;
  /**
   * Personalized items the owner turned off in step 1 (at most 200): left out of every copy with
   * reason `turned_off`. Absent on content saved before round 6, which reads as none.
   */
  excluded_item_ids?: string[];
  branding: RecipientBrandApi;
  message: MessageTemplatesApi;
  /**
   * Where the logo comes from: each row's own (`row`, the default) or the distribution's (`distribution`:
   * its branding logo, else the default kit's). Absent on content saved before the choice existed.
   */
  logo_source?: LogoSourceApi;
}

export type LogoSourceApi = "row" | "distribution";

// ─── Schedule ─────────────────────────────────────────────────────────────────

export type ScheduleKindApi = "once" | "daily" | "weekly" | "monthly" | "quarterly";

export type ScheduleEndsApi =
  | { kind: "never" }
  | { kind: "on"; date: string }
  | { kind: "after"; runs: number };

/** "first" … "fourth" or the last one of the month. */
export type MonthlyNthApi = 1 | 2 | 3 | 4 | "last";

/**
 * Monthly and quarterly only, instead of `day_of_month`: "the first Tuesday" (weekday 0 = Monday)
 * or "the first / last business day" (Monday to Friday; no holiday calendar).
 */
export type MonthlyRuleApi =
  | { kind: "weekday"; nth: MonthlyNthApi; weekday: number }
  | { kind: "business_day"; nth: 1 | "last" };

/** null schedule = send on demand only ("Send now"). */
export interface DistributionScheduleApi {
  kind: ScheduleKindApi;
  /** Every N days / weeks / months / quarters (1–52). Ignored for `once`. */
  every: number;
  /** "08:00", 24h, in `timezone`. */
  at: string;
  /** IANA name. Times are the sender's, not each recipient's. */
  timezone: string;
  /** weekly: 0 = Monday … 6 = Sunday. */
  weekdays: number[];
  /** monthly / quarterly; days 29–31 fall back to the month's last day. null when `monthly_rule` is set. */
  day_of_month: number | "last" | null;
  /**
   * monthly / quarterly, replacing `day_of_month` (which must then be null, else 422). Absent or
   * null: `day_of_month` applies. Quarterly rules apply in the `quarter_month` of each quarter.
   */
  monthly_rule?: MonthlyRuleApi | null;
  /** quarterly: 1st, 2nd or 3rd month of the quarter. */
  quarter_month: 1 | 2 | 3 | null;
  /** yyyy-mm-dd; for `once`, the send date. */
  starts_on: string;
  ends: ScheduleEndsApi;
}

export interface SchedulePreviewRequestApi {
  schedule: DistributionScheduleApi;
  /** 1–10 */
  count: number;
  /** Sends already made, so "after N" previews correctly for an active distribution. */
  runs_done?: number;
}

export interface SchedulePreviewApi {
  timezone: string;
  /** ISO-8601 with offset. */
  runs: string[];
  /** The last send if the schedule ends within reach, else null. */
  last_run: string | null;
  /** A send whose local offset differs from the first one (daylight saving). */
  offset_changes: { at: string; note: string }[];
}

// ─── Distribution ─────────────────────────────────────────────────────────────

/** The check codes api-service sends (lower_snake); any other code still renders from `message`. */
export const DISTRIBUTION_CHECK_CODES = [
  "no_split",
  "no_values",
  "no_recipients",
  "recipients_to_fix",
  "nothing_to_send",
  "no_formats",
  "schedule_error",
  "coverage_unknown",
  "uncovered_values",
] as const;
export type DistributionCheckCodeApi = (typeof DISTRIBUTION_CHECK_CODES)[number];

export interface CheckApi {
  /** A DistributionCheckCodeApi, or a code the UI doesn't know yet. */
  code: string;
  /** Says what's wrong and how to fix it. */
  message: string;
  /** Wizard step that fixes it (1–5). */
  step: 1 | 2 | 3 | 4 | 5;
}

export interface ChecksApi {
  /** Empty ⇔ can activate. */
  blockers: CheckApi[];
  warnings: CheckApi[];
  last_test: { sent_at: string; value: string; sent_to: string } | null;
}

export interface RunCountsApi {
  copies: number;
  sent: number;
  failed: number;
  skipped: number;
  held: number;
}

export type RunTriggerApi = "now" | "schedule" | "test" | "preview";
export type RunStatusApi = "queued" | "running" | "succeeded" | "partly_failed" | "failed";

export interface RunSummaryApi {
  id: string;
  trigger: RunTriggerApi;
  status: RunStatusApi;
  scheduled_for: string | null;
  started_at: string | null;
  finished_at: string | null;
  counts: RunCountsApi;
  /** Round 6 (WP3): who started it, moved up from RunApi; null for a scheduled run. */
  requested_by: UserRefApi | null;
  /** When the run was queued (ISO). A queued run has no `started_at` yet. Absent on older builds. */
  queued_at?: string | null;
}

// ─── Round 6 · runs list filters (WP3, addendum §3.4) ─────────────────────────

/** GET runs `?trigger=…&status=…`, each repeatable; empty or absent means every run. */
export interface RunListFiltersApi {
  trigger?: RunTriggerApi[];
  status?: RunStatusApi[];
}

export interface DistributionSummaryApi {
  id: string;
  story_id: string;
  name: string;
  status: DistributionStatusApi;
  split_label: string | null;
  recipient_count: number;
  channels: ChannelApi[];
  formats: FormatApi[];
  template_name: string | null;
  schedule: DistributionScheduleApi | null;
  next_run_at: string | null;
  last_run: RunSummaryApi | null;
  updated_at: string;
  /**
   * Round 6 (WP3): sends so far (`story_distribution.runs_count`: Send now and scheduled runs, not
   * tests or previews); the schedule's "end after N sends" counts these. Not the runs list's total.
   */
  run_count: number;
  /** When it was archived (ISO) and by whom; null while it isn't archived. */
  archived_at?: string | null;
  archived_by_user_id?: string | null;
  archived_by?: UserRefApi | null;
  /** When it was paused (ISO) and by whom; null when it isn't paused, or was paused before this was recorded. */
  paused_at?: string | null;
  paused_by_user_id?: string | null;
  paused_by?: UserRefApi | null;
}

export interface DistributionApi extends DistributionSummaryApi {
  split: SplitFieldApi | null;
  /** Values of the split field left out on purpose. */
  excluded_values: string[];
  /** Skip a value whose copy would be empty. */
  skip_empty_values: boolean;
  uncovered_policy: UncoveredPolicyApi;
  /** Notes, links and stills the owner chose to send as they are. */
  send_unfiltered_item_ids: string[];
  recipient_source: RecipientSourceInfoApi | null;
  recipient_summary: RecipientSummaryApi;
  coverage: CoverageApi | null;
  content: DistributionContentApi;
  schedule_state: "none" | "synced" | "error";
  schedule_error: string | null;
  runs_count: number;
  checks: ChecksApi;
  permissions: { can_edit: boolean; can_run: boolean };
  created_by: UserRefApi | null;
  created_at: string;
  activated_at: string | null;
}

export interface DistributionListApi {
  items: DistributionSummaryApi[];
}

export interface DistributionCreateApi {
  /** 1–120 chars. */
  name: string;
  /** Absent: no split field, so everyone gets the whole Story at their own clearance ("Deliver Story"). */
  split?: SplitFieldApi;
}

/** Each top-level key replaces wholesale; absent = unchanged. Drafts autosave through PATCH. */
export interface DistributionPatchApi {
  name?: string;
  split?: SplitFieldApi;
  excluded_values?: string[];
  skip_empty_values?: boolean;
  uncovered_policy?: UncoveredPolicyApi;
  send_unfiltered_item_ids?: string[];
  content?: DistributionContentApi;
}

/** draft|paused → active (409 when checks.blockers isn't empty); active → paused. */
export interface DistributionStatusRequestApi {
  status: "active" | "paused";
}

// ─── Runs, slices, deliveries ─────────────────────────────────────────────────

/**
 * `preview` builds one recipient's copy without sending (artifacts optional) — the exact copy;
 * `test` sends one recipient's copy to `test_targets`, or to the caller's own email when none.
 */
export interface RunRequestApi {
  trigger: "now" | "test" | "preview";
  /** Recipient positions; required for test and preview (max 5). */
  positions?: number[];
  /** preview only: also render the files. */
  artifacts?: boolean;
  /**
   * test only, at most `MAX_TEST_TARGETS`. Empty or absent: the caller's own email. 422 when a
   * target is a Slack channel and Slack isn't connected, or an email outside the organisation
   * while the audience is `inside`.
   */
  test_targets?: TestTargetApi[] | null;
}

export interface RunAcceptedApi {
  run_id: string;
}

/** `POST …/runs/{run_id}/retry-failed`: the new run and how many deliveries it sends again. */
export interface RetryAcceptedApi {
  run_id: string;
  delivery_count: number;
}

export type SliceStatusApi = "pending" | "rendering" | "rendered" | "built" | "failed";

export interface FileRefApi {
  format: FormatApi;
  filename: string;
  bytes: number | null;
}

export interface SliceSummaryApi {
  id: string;
  value: string;
  status: SliceStatusApi;
  template_id: string;
  theme_id: string | null;
  files: FileRefApi[];
  left_out: LeftOutTileApi[];
  error: string | null;
}

export type DeliveryStatusApi = "pending" | "sending" | "sent" | "failed" | "skipped" | "held";

export interface DeliveryApi {
  id: string;
  slice_id: string | null;
  recipient: {
    position: number;
    name: string;
    value: string;
    channel: ChannelApi;
    address: string;
    cc: string[];
  };
  formats: FormatApi[];
  status: DeliveryStatusApi;
  /**
   * What went wrong and how to fix it; shown verbatim. On a `sent` delivery it is only a note
   * ("PowerPoint isn't available for a memo; the PDF was sent.") and never makes it a failure.
   */
  error: string | null;
  sent_at: string | null;
}

export interface RunApi extends RunSummaryApi {
  error: string | null;
  /** When the data behind this run was loaded. */
  data_as_of: string | null;
}

export interface RunDetailApi {
  run: RunApi;
  slices: SliceSummaryApi[];
  deliveries: DeliveryApi[];
}

export interface RunPageApi {
  items: RunSummaryApi[];
  total: number;
  offset: number;
  limit: number;
}

/** A tile as drawn in a copy. Rows are already masked at the recipient clearance. */
export type CopyTileApi =
  | {
      kind: "chart";
      item_id: string;
      position: number;
      /** Canvas width, 1–12 grid columns. */
      width: number;
      title: string;
      caption: string | null;
      chart_type: string;
      /** Vega-Lite v5 without data.values; draw with `rows`. */
      vega_spec: Record<string, unknown>;
      rows: Record<string, unknown>[];
      masked_columns: string[];
    }
  | {
      kind: "kpi";
      item_id: string;
      position: number;
      width: number;
      title: string;
      formatted: string | null;
      delta_formatted: string | null;
      direction: "up" | "down" | "flat" | null;
      comparison_label: string | null;
      masked: boolean;
    }
  | {
      kind: "table";
      item_id: string;
      position: number;
      width: number;
      title: string;
      columns: { name: string; label: string; masked: boolean }[];
      rows: Record<string, unknown>[];
    }
  | { kind: "note"; item_id: string; position: number; width: number; text: string }
  | { kind: "link"; item_id: string; position: number; width: number; url: string; title: string };

export interface SliceDetailApi {
  slice: SliceSummaryApi;
  tiles: CopyTileApi[];
  /** AI summary of only this slice's numbers; null when off or not generated. */
  summary: string | null;
  data_as_of: string | null;
  /**
   * The composed message of each recipient of this copy, by position (every run, preview and
   * test included). Empty for copies made before messages were stored; absent on older answers.
   */
  messages?: CopyMessageApi[];
  /** The brand the copy was drawn with; null when unknown. Absent on older answers. */
  brand?: SliceBrandApi | null;
}

export interface SignedFileApi {
  url: string;
  expires_at: string;
  filename: string;
}

// ─── Errors ───────────────────────────────────────────────────────────────────

/** 422 `details` on PUT recipients / template writes (read with getApiErrorDetails). */
export interface RowErrorDetailApi {
  position: number;
  field: string;
  code: string;
  message: string;
}

// ─── Round 6 · message variables (FE-gallery; addendum §3.3) ──────────────────

/** A KPI tile's variable. `token` is magic-core `kpi_token(name)`: `kpi:<name>`, lower-cased. */
export interface TokenKpiApi {
  token: string;
  /** The tile's title as the canvas shows it, trimmed. */
  name: string;
}

/**
 * GET /{distribution_id}/tokens: every variable the distribution's message may use. Content saves
 * and activation check the message against the same list (magic-core `known_tokens`).
 */
export interface TokenListApi {
  /** magic-core `TOKENS`, in order. */
  builtin: string[];
  /** The stored recipients' `extra` keys, lower-cased. */
  columns: string[];
  /** One per titled KPI tile of the Story. */
  kpis: TokenKpiApi[];
}

// ─── Round 6 · exact preview and send test (FE-preview) ───────────────────────

/** A test run goes to at most this many places (magic-core `MAX_TEST_TARGETS`). */
export const MAX_TEST_TARGETS = 5;

/** Where a test copy goes: an email address or a Slack channel, checked like a recipient's. */
export interface TestTargetApi {
  channel: "email" | "slack";
  address: string;
}

/**
 * What one recipient of a copy is sent, or would be for a preview (magic-core `CopyMessage`):
 * the owner's message with its tokens filled in, as plain text. A masked value reads as its
 * hidden wording. `subject` and `body` are an email's, `slack_text` a Slack post's.
 */
export interface CopyMessageApi {
  position: number;
  channel: ChannelApi;
  subject: string | null;
  body: string | null;
  slack_text: string | null;
}

/** The brand a copy was drawn with, resolved from the row, the content and the brand kit. */
export interface SliceBrandApi {
  name: string | null;
  /** "#RRGGBB" */
  accent: string | null;
  mark: string | null;
  /** The palette's colours, in order ("#RRGGBB"); empty without a palette. */
  palette: string[];
  /** The copy's own https logo, or a 15-minute signed GET of its brand-kit logo. */
  logo_url: string | null;
}

// ─── Round 6 · WP5 recipients from the data model (FE-recipients) ─────────────
//
//   GET  "/model-fields"                → ModelFieldListApi
//   POST "/model-rows"                  ModelRowsRequestApi → 202 { request_id }
//   GET  "/model-rows/{request_id}"     → ModelRowsApi (polled until `done` or `failed`)
//
// magic-core 2.41.0: `ModelColumn`, `ModelCondition`, `ModelRecipientSource` (schemas/distribution/setup.py)
// and `ReadModelRows` → `ModelRows` (temporal/workflows/story_distribution.py).

/**
 * Where a model attribute's values go in a recipient row: a row column, `brand.<key>`, `extra.<key>`
 * (a message token), or `match` (the key a fill-in joins on; never in a saved source).
 */
export type RecipientTargetApi =
  | "match"
  | "value"
  | "name"
  | "channel"
  | "address"
  | "cc"
  | "formats"
  | "template_id"
  | "theme_id"
  | `brand.${"name" | "logo_url" | "accent" | "mark"}`
  | `extra.${string}`;

export interface ModelColumnApi {
  target: RecipientTargetApi;
  /** The model attribute read into it. */
  field: FieldRefApi;
}

/** Keep the rows whose attribute equals one of the values (1–1000 values; up to 10 conditions). */
export interface ModelConditionApi {
  field: FieldRefApi;
  values: string[];
}

/** Up to 21 columns, each target once. */
export const MAX_MODEL_COLUMNS = 21;
export const MAX_MODEL_CONDITIONS = 10;
export const MAX_MODEL_CONDITION_VALUES = 1_000;

/**
 * Recipients read from the data model: one row per distinct combination of the mapped attributes,
 * read at the owner's own clearance. Maps at least `value` and `address`, never `match`.
 */
export interface ModelRecipientSourceApi {
  model_release_id: string;
  columns: ModelColumnApi[];
  where: ModelConditionApi[];
  /** Each row's channel when no column maps `channel`. */
  channel: "email" | "slack";
  /** Read the model again before every send; CSV and manual rows stay. */
  refresh_before_send: boolean;
}

/**
 * Every attribute of the Story's model a recipient column can be read from (people's names, emails
 * and teams, as well as split candidates). `above_clearance` and `blocked_reason` are relative to
 * the caller's own clearance: an imported cell above it comes back masked.
 */
export interface ModelFieldListApi {
  items: SplitFieldOptionApi[];
  /** The caller's own clearance. */
  clearance: SensitivityClassApi;
}

export interface ModelRowsRequestApi {
  columns: ModelColumnApi[];
  where?: ModelConditionApi[];
  /** 1–5000; api-service's default is `ReadModelRows.limit` (1000). */
  limit?: number;
}

/** A model-rows read, as `value-stats`: `pending` until it is `done` or `failed`. */
export interface ModelRowsApi {
  request_id: string;
  status: "pending" | "done" | "failed";
  /** One per distinct row; keyed by target, null when blank or masked. */
  rows: Record<string, string | null>[];
  /** Targets above the caller's clearance: null in every row. */
  masked: string[];
  /** More rows matched than `limit`. */
  truncated: boolean;
  /** Set when `failed`; the message is shown verbatim. */
  error: { code: string; message: string } | null;
}

/** POST …/preview-render body: one copy built without a run. */
export interface PreviewRenderRequestApi {
  position: number;
  artifacts?: boolean;
  wait_ms?: number;
  /** Starts a fresh render instead of reusing the finished one for the same key. */
  force?: boolean;
}

export type PreviewRenderStatusApi = "running" | "ready" | "failed";

/** POST/GET …/preview-render: the copy (no run, slice or file rows) and a 15 min signed PDF link. */
export interface PreviewRenderApi {
  render_id: string;
  status: PreviewRenderStatusApi;
  error: string | null;
  detail: SliceDetailApi | null;
  pdf_url: string | null;
  pdf_expires_at: string | null;
  /** Every file of the copy, each signed for 15 min. */
  files?: PreviewRenderFileApi[];
}

export interface PreviewRenderFileApi {
  format: "pdf" | "pptx";
  filename: string;
  size: number;
  url: string;
  expires_at: string;
}
